import { forwardRef, isValidElement, ReactElement } from 'react';
import { Tooltip as MuiTooltip, TooltipProps as MuiTooltipProps } from '@mui/material';

/**
 * Tooltip component for displaying contextual help text.
 *
 * Provides helpful information when users hover over or focus on elements.
 * Supports auto-wrapping of disabled elements via the `wrapDisabled` prop.
 *
 * @example
 * // Basic usage
 * <Tooltip title="Help text">
 *   <Button>Hover me</Button>
 * </Tooltip>
 *
 * @example
 * // With arrow (default)
 * <Tooltip title="Edit item" arrow>
 *   <IconButton><EditIcon /></IconButton>
 * </Tooltip>
 *
 * @example
 * // Without arrow
 * <Tooltip title="Info" arrow={false}>
 *   <Button>No Arrow</Button>
 * </Tooltip>
 *
 * @example
 * // Auto-wrapping disabled elements
 * <Tooltip title="Cannot delete" wrapDisabled>
 *   <Button disabled>Delete</Button>
 * </Tooltip>
 *
 * @example
 * // Custom placement
 * <Tooltip title="Tooltip on right" placement="right">
 *   <IconButton><InfoIcon /></IconButton>
 * </Tooltip>
 *
 * @example
 * // Controlled visibility
 * <Tooltip title="Always visible" open={true}>
 *   <Button>Open</Button>
 * </Tooltip>
 */
export interface TooltipProps extends Omit<MuiTooltipProps, 'placement'> {
  /**
   * Tooltip position relative to the trigger element.
   * Only cardinal directions are supported for simplicity.
   * @default 'top'
   */
  placement?: 'top' | 'right' | 'bottom' | 'left';

  /**
   * Whether to display an arrow pointing to the trigger element.
   * The arrow provides a visual connection between the tooltip and its trigger.
   * @default true
   */
  arrow?: boolean;

  /**
   * Auto-wrap children in span if they are disabled.
   *
   * MUI Tooltips don't work with disabled elements because they don't trigger
   * mouse events. This prop automatically wraps disabled children in a span
   * element to make the tooltip work correctly.
   *
   * **Note:** If you're wrapping disabled elements manually with `<span>`,
   * you don't need this prop.
   *
   * @default false
   *
   * @example
   * // Without wrapDisabled (tooltip won't work)
   * <Tooltip title="Cannot edit">
   *   <Button disabled>Edit</Button>
   * </Tooltip>
   *
   * @example
   * // With wrapDisabled (tooltip works)
   * <Tooltip title="Cannot edit" wrapDisabled>
   *   <Button disabled>Edit</Button>
   * </Tooltip>
   *
   * @example
   * // Manual wrapping (legacy pattern, still works)
   * <Tooltip title="Cannot edit">
   *   <span><Button disabled>Edit</Button></span>
   * </Tooltip>
   */
  wrapDisabled?: boolean;
}

/**
 * Tooltip component for displaying contextual help text.
 *
 * A thin wrapper around Material-UI's Tooltip with enhanced developer experience:
 * - Simplified placement options (4 cardinal directions)
 * - Arrow enabled by default for better visual connection
 * - Auto-wrapping of disabled elements via `wrapDisabled` prop
 * - Strict TypeScript types for better IntelliSense
 *
 * @see https://mui.com/material-ui/react-tooltip/ for additional MUI Tooltip documentation
 */
export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  ({ arrow = true, placement = 'top', wrapDisabled = false, children, ...props }, ref) => {
    // Auto-wrap disabled children in span for MUI compatibility
    let wrappedChildren = children;

    // Check if child is a valid React element with disabled prop
    if (
      wrapDisabled &&
      isValidElement(children) &&
      (children as ReactElement<{ disabled?: boolean }>).props.disabled
    ) {
      wrappedChildren = <span style={{ display: 'inline-block' }}>{children}</span>;
    }

    return (
      <MuiTooltip ref={ref} arrow={arrow} placement={placement} {...props}>
        {wrappedChildren}
      </MuiTooltip>
    );
  }
);

Tooltip.displayName = 'Tooltip';
